import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LoggingService } from '@services/logging.service';
import { User } from '@models/user.model';
import { User as GraphUser } from '@microsoft/microsoft-graph-types';
import { Router } from '@angular/router';
import { LicensingCountry } from '@models/licensingCountry.model';
import { ReplaySubject, firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { access } from 'fs';
@Injectable()
export class UserService {
  baseUrl: string;

  public ensuredUser: User;
  public allCountryList: LicensingCountry[] = [];
  public allCountryList$ = new ReplaySubject<LicensingCountry[]>(1);

  constructor(
    private http: HttpClient,
    private router: Router,
    private logger: LoggingService,
    private authService: AuthService,
  ) {}

  public async GetAllUsers(): Promise<User[]> {
    this.logger.info(this.constructor.name, 'GetAllUsers', 'Start');
    let users: User[] = [];
    users = <User[]>(
      await firstValueFrom(this.http.get<User[]>('/api/user')).catch((e) => this.logger.httpError(this.constructor.name, 'GetAllUsers', e))
    );

    if (!users) {
      users = [];
    } else {
      this.logger.info(this.constructor.name, 'GetAllUsers', 'Results', users);
    }
    return users;
  }

  public async EnsureUser(graphUser: GraphUser): Promise<User> {
    this.logger.info(this.constructor.name, 'EnsureUser', 'Start', JSON.stringify(graphUser));

    let ensuredUser: User = null;
    ensuredUser = <User>(
      await firstValueFrom(this.http.post<User>('/api/user/ensureuser', graphUser)).catch((e) =>
        this.logger.httpError(this.constructor.name, 'EnsureUser', e),
      )
    );

    this.logger.info(this.constructor.name, 'EnsureUser', JSON.stringify(ensuredUser));
    this.ensuredUser = ensuredUser;
    return ensuredUser;
  }

  public async IsToolAccessAvailable(graphUser: GraphUser): Promise<boolean> {
    this.logger.info(this.constructor.name, 'IsToolAccessAvailable', 'Start', JSON.stringify(graphUser));
    let isToolAccessAvailable = false;

    isToolAccessAvailable = <boolean>(
      await firstValueFrom(this.http.post<boolean>('/api/user/IsToolAccessAvailable', graphUser)).catch((e) =>
        this.logger.httpError(this.constructor.name, 'IsToolAccessAvailable', e),
      )
    );
    return isToolAccessAvailable;
  }

  async UpdateUser(user: User): Promise<User> {
    this.logger.info(this.constructor.name, 'UpdateUser', 'Start');

    if (user.userId) {
      await firstValueFrom(this.http.put<User>('/api/User', user)).catch((e) => {
        this.logger.httpError(this.constructor.name, 'UpdateUser', e);
        if (e.status === 401) {
          this.router.navigate(['nostudyaccess']);
        }
      });
    }

    this.logger.info(this.constructor.name, 'UpdateUser', JSON.stringify(user));
    return user;
  }

  async UpdateUserSupport(user: User): Promise<User> {
    this.logger.info(this.constructor.name, 'UpdateUser', 'Start');
    let graphToken: string = await this.authService.getAzureADAccessToken();

    if (user.userId) {
      let headers = new HttpHeaders({ GraphToken: graphToken });
      await firstValueFrom(this.http.put<User>('/api/User/PutSupport', user, { headers: headers })).catch((e) => {
        this.logger.httpError(this.constructor.name, 'UpdateUser', e);
        if (e.status === 401) {
          this.router.navigate(['nostudyaccess']);
        }
        if (e.status === 400) {
          user = null; // in case of bad request return null to raise an error
        }
      });
    }

    this.logger.info(this.constructor.name, 'UpdateUser', JSON.stringify(user));
    return user;
  }

  public async getAllUserCountryList(): Promise<LicensingCountry[]> {
    this.logger.info(this.constructor.name, 'getAllUserCountryList', 'Start');
    let countries: LicensingCountry[] = [];
    countries = <LicensingCountry[]>(
      await firstValueFrom(this.http.get<LicensingCountry[]>('/api/master/GetCountryList')).catch((e) =>
        this.logger.httpError(this.constructor.name, 'getAllUserCountryList', e),
      )
    );

    if (!countries) {
      countries = [];
    } else {
      // this.logger.info(this.constructor.name, 'getAllUserCountryList', 'Results', countries);
    }
    this.allCountryList = countries;
    this.allCountryList$.next(this.allCountryList);
    return countries;
  }

  public async updateUserCountry(countryId: number) {
    this.logger.info(this.constructor.name, 'updateUserCountry', 'Start');

    const result = <any>await firstValueFrom(this.http.get<any>(`/api/study/UpdateUserCountry/${countryId}`)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'updateUserCountry', e);
    });

    return result;
  }

  public async updateUserCountrySupport(userId: number, countryId: number) {
    this.logger.info(this.constructor.name, 'updateUserCountrySupport', 'Start');

    const result = <any>await firstValueFrom(this.http.get<any>(`/api/study/UpdateUserCountrySupport/${userId}/${countryId}`)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'updateUserCountrySupport', e);
    });

    return result;
  }

  public async getCountryId(graphUser: GraphUser): Promise<number> {
    this.logger.info(this.constructor.name, 'GetCountryId', 'Start');

    const result = <number>await firstValueFrom(this.http.post<number>('/api/user/GetCountryId', graphUser)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetCountryId', e);
    });

    return result;
  }

  public async getCountryIdSupport(graphUser: GraphUser): Promise<number> {
    this.logger.info(this.constructor.name, 'GetCountryIdSupport', 'Start');

    const result = <number>await firstValueFrom(this.http.post<number>('/api/user/GetCountryIdSupport', graphUser)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetCountryIdSupport', e);
    });

    return result;
  }

  public async getUserByActiveDirectoryId(guid: any): Promise<any> {
    this.logger.info(this.constructor.name, 'getUserByActiveDirectoryId', 'Start');

    try {
      const result = await this.http.get<any>(`/api/user/GetUserByActiveDirectoryId/${guid}`).toPromise();
      return result;
    } catch (e) {
      this.logger.httpError(this.constructor.name, 'getUserByActiveDirectoryId', e);
      throw e;
    }
  }

  public async getUserByActiveDirectoryIdCurrent(guid: any): Promise<any> {
    this.logger.info(this.constructor.name, 'getUserByActiveDirectoryIdCurrent', 'Start');

    try {
      const result = await this.http.get<any>(`/api/user/GetUserByActiveDirectoryId_support/${guid}`).toPromise();
      return result;
    } catch (e) {
      return null; // or return an empty object if you prefer: return {};
    }
  }

  public async getDatabaseNamesByCountry(licensingCountryID: number): Promise<string[]> {
    try {
      const result = await this.http.get<string[]>(`/api/User/GetDatabaseNamesByCountry/${licensingCountryID}`).toPromise();
      return result;
    } catch (e) {
      alert(1);
      this.logger.httpError(this.constructor.name, 'getDatabaseNamesByCountry', e);
      throw e;
    }
  }

  public async addUser(graphUser: GraphUser): Promise<User> {
    this.logger.info(this.constructor.name, 'addUser', 'Start');

    try {
      let graphToken: string = await this.authService.getAzureADAccessToken();
      let headers = new HttpHeaders({ GraphToken: graphToken });
      const ensuredUser: User = await this.http.post<User>('/api/user/AddUser', graphUser, { headers: headers }).toPromise();
      this.ensuredUser = ensuredUser;
      return ensuredUser;
    } catch (error) {
      this.logger.httpError(this.constructor.name, 'addUser', error);
      throw error;
    }
  }
}
